import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { OrgSettings } from '@dg/core';
import { api, type Me } from '../api.ts';

export function Settings({ me }: { me: Me }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['org-settings'], queryFn: () => api<OrgSettings>('/api/org/settings') });
  const [s, setS] = useState<OrgSettings | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => setS(q.data ?? null), [q.data]);
  if (!s) return <p className="muted">{t('common.loading')}</p>;
  const readOnly = me.role !== 'owner';

  async function save(e: React.FormEvent) {
    e.preventDefault();
    await api('/api/org/settings', { method: 'PUT', body: s });
    await qc.invalidateQueries({ queryKey: ['org-settings'] });
    setSaved(true);
  }

  const colorKeys = [['ok', 'settings.ok'], ['warn', 'settings.warn'], ['alert', 'settings.alert'], ['stale', 'settings.staleColor'], ['neutral', 'settings.neutral']] as const;
  return (
    <form className="stack narrow" onSubmit={save}>
      <h1>{t('settings.title')}</h1>
      <fieldset className="card stack" disabled={readOnly}>
        <legend><h2>{t('settings.staleTitle')}</h2></legend>
        <div className="field"><label htmlFor="sa">{t('settings.staleAfter')}</label>
          <input id="sa" type="number" min={1} max={10080} value={s.stale.after_minutes} onChange={(e) => setS({ ...s, stale: { ...s.stale, after_minutes: Number(e.target.value) || 1 } })} /></div>
        <div className="field"><label htmlFor="sn">{t('settings.staleNeedle')}</label>
          <select id="sn" value={s.stale.needle} onChange={(e) => setS({ ...s, stale: { ...s.stale, needle: e.target.value as 'hold' | 'min' } })}>
            <option value="hold">{t('settings.staleHold')}</option><option value="min">{t('settings.staleMin')}</option>
          </select></div>
      </fieldset>
      <fieldset className="card stack" disabled={readOnly}>
        <legend><h2>{t('settings.colors')}</h2></legend>
        <div className="row">
          {colorKeys.map(([k, label]) => (
            <div className="field" key={k}><label htmlFor={`c-${k}`}>{t(label)}</label>
              <input id={`c-${k}`} type="color" value={s.colors[k]} onChange={(e) => setS({ ...s, colors: { ...s.colors, [k]: e.target.value.toUpperCase() } })} /></div>
          ))}
        </div>
      </fieldset>
      {!readOnly && <div className="row"><button className="btn">{t('settings.save')}</button>{saved && <span className="ok" role="status">{t('settings.saved')}</span>}</div>}
    </form>
  );
}
